/**
 * The replication check as Studio's exceptions: every brand block whose rows do not give the base
 * back (`replicateRows`) is raised. A line that explains the base is offered as a row of its own
 * (taken, its products stand as that row: `collapse4`); a block no line explains is a question.
 * Read off the base planogram only, never off a layout this pipeline produced. A pack size tagged
 * one tier that the base stands with another tier's packs is asked about too (`sizeTierFlags`), and
 * so is a size-tier brand block whose base shows neither layout (`sizeLayoutFlags`).
 */
import { buildLayout } from "./buildLayout";
import { collapse4, sanitizeCdtValue } from "./collapse4";
import { ROW_LINE_PREFIX, rowLineId, rowQuestionId } from "./rowLines";
import { sizeTierFlags } from "./sizeTierQuestions";
import { SIZE_LAYOUT_PREFIX } from "./sizeTiers";
import { applyFix } from "./studioTransactions";
import { phrase } from "./flagWords";
import {
  type Flag,
  PATH_SEPARATOR,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
} from "./studioTypes";

const products = (n: number) => (n === 1 ? "product" : "products");

export { SIZE_LAYOUT_PREFIX };

const listed = (names: readonly string[]): string =>
  names.length <= 1
    ? (names[0] ?? "")
    : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;

/** A size-tier brand block the base lays out neither way (`sizeLayoutOf` reads it unclear): its
 *  sub-brands neither on rows of their own nor side by side in columns (base 210's Tide
 *  Unscented, a size ladder whose sub-brands take turns shelf by shelf). A question for the
 *  merchant, answered with one of three layouts; Finalize waits for it. */
export const sizeLayoutFlags = (
  readings: readonly {
    column: string;
    sizeLayoutQuestion?: { subBrands: readonly string[]; sizesRunUp: boolean };
  }[],
  itemValues: Readonly<Record<string, readonly string[]>>,
): Flag[] =>
  readings.flatMap((reading) => {
    const asked = reading.sizeLayoutQuestion;
    if (!asked) return [];
    const skuIds = Object.entries(itemValues)
      .filter(([, values]) => values.slice(0, 2).join(PATH_SEPARATOR) === reading.column)
      .map(([skuId]) => skuId);
    const scopePath = reading.column.split(PATH_SEPARATOR);
    const block = scopePath[scopePath.length - 1] ?? reading.column;
    const shape =
      (asked.sizesRunUp ? "its sizes run small to large and " : "") +
      `the sub-brands ${listed(asked.subBrands)} take turns shelf by shelf`;
    return [
      {
        id: `${SIZE_LAYOUT_PREFIX}${reading.column}`,
        kind: "ambiguous" as const,
        severity: "blocking" as const,
        scopePath,
        title: `${block}: on the base ${shape}. How should the store planograms lay it out?`,
        detail:
          `On the base ${block}'s sub-brands stand neither on shelves of their own nor side by ` +
          "side as vertical blocks, so Studio cannot tell which layout is meant. Keep the base's " +
          "product order, use horizontal blocking (each sub-brand on shelves of its own) or " +
          "vertical blocking (each sub-brand its own vertical block).",
        skuIds,
        anchorSkuId: skuIds[0] ?? "",
        evidence: { notes: [`Sub-brands, top to bottom: ${asked.subBrands.join(", ")}`] },
        fix: null,
        fixLabel: null,
        words: {
          title: phrase(asked.sizesRunUp ? "sizeLayoutTitle" : "sizeLayoutTitleUnsorted", {
            brand: block,
            subBrands: { list: asked.subBrands },
          }),
          detail: [phrase("sizeLayoutDetail", { brand: block })],
        },
        question: {
          brand: block,
          newBrand: block,
          skuCount: skuIds.length,
          where: [],
          mainWhere: [],
          sizeLayout: { subBrands: [...asked.subBrands], sizesRunUp: asked.sizesRunUp },
        },
      },
    ];
  });

export const rowLineFlags = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
): Flag[] => {
  const layout = buildLayout(items, draft, runs);
  const collapsed = collapse4(items, draft, runs, layout, layout.walkOrder);
  const { tierReadings, contract } = collapsed;
  const bySku = new Map(items.map((item) => [item.skuId, item]));
  /** "Small on shelves 1, 2, 6; Medium on 3-5": where each row of a block stands on the base. */
  const rowsOnBase = (column: string): { row: string; shelves: number[] }[] => {
    const shelves = new Map<string, Set<number>>();
    for (const [skuId, values] of Object.entries(contract.itemValues)) {
      if (values.slice(0, 2).join(PATH_SEPARATOR) !== column) continue;
      const row = values[2] || "(no row)";
      const set = shelves.get(row) ?? new Set<number>();
      for (const placement of bySku.get(skuId)?.placements ?? []) set.add(placement.shelf);
      shelves.set(row, set);
    }
    return [...shelves].map(([row, set]) => ({
      row,
      shelves: [...set].sort((a, b) => a - b),
    }));
  };
  /** An attribute as the merchant reads it: its caption on the draft ("Oxi", "Pack type"). */
  const labelOf = (attr: string) => draft.spine.find((level) => level.attr === attr)?.label ?? attr;
  const flags: Flag[] = [];
  for (const reading of tierReadings) {
    if (reading.replicates !== false) continue;
    const scopePath = reading.column.split(PATH_SEPARATOR);
    for (const line of reading.lines ?? []) {
      const count = line.skuIds.length;
      const side = phrase(line.side === "top" ? "topRow" : "bottomRow");
      const named = `${labelOf(line.attr)} "${line.value}"`;
      flags.push({
        // Named as collapse4 looks the taken line up: by the row's cleaned-up CDT value.
        id: rowLineId(reading.column, sanitizeCdtValue(line.name)),
        kind: "split",
        severity: "blocking",
        scopePath,
        title:
          `${reading.column}: its rows do not give the base back; the base stands its ` +
          `${named} ${products(count)} as the ${line.side} row → "${line.name}" as a row of ` +
          `its own (${count} ${count === 1 ? "SKU" : "SKUs"})`,
        detail:
          `Sorted by the rows Studio reads, ${reading.column} does not stack the way the base ` +
          `does. On the base its ${named} ${products(count)} stand as the block's ${line.side} ` +
          `row. As a row of their own, "${line.name}", a store planogram keeps them there.`,
        skuIds: line.skuIds,
        anchorSkuId: line.skuIds[0],
        evidence: { notes: [`${line.attr}: ${line.value}`, `The block's ${line.side} row`] },
        fix: null,
        fixLabel: `Make "${line.name}" a row of its own`,
        words: {
          title: phrase(
            "rowLineTitle",
            {
              block: reading.column,
              attribute: labelOf(line.attr),
              value: line.value,
              side,
              line: line.name,
            },
            count,
          ),
          detail: [
            phrase("rowLineDetail", {
              block: reading.column,
              attribute: labelOf(line.attr),
              value: line.value,
              side,
              line: line.name,
            }),
          ],
          stop: true,
          fixLabel: phrase("rowLineFix", { line: line.name }),
        },
      });
    }
    if (reading.unexplained) {
      const skuIds = Object.entries(contract.itemValues)
        .filter(([, values]) => values.slice(0, 2).join(PATH_SEPARATOR) === reading.column)
        .map(([skuId]) => skuId);
      const baseRows = rowsOnBase(reading.column);
      flags.push({
        id: rowQuestionId(reading.column),
        kind: "inversion",
        severity: "blocking",
        scopePath,
        title: `${reading.column}: its rows do not give the base back, and no product line explains how the base stacks it`,
        detail:
          `Sorted by the rows Studio reads (${reading.rowOrder.join(", ")}), ${reading.column} ` +
          `does not stack the way the base does: on the base ${baseRows
            .map(({ row, shelves }) => `${row} on shelves ${shelves.join(", ")}`)
            .join("; ")}. No ` +
          "attribute value names the products the base stands apart. Check the item master, or " +
          "tell Studio how this block is laid out.",
        skuIds,
        anchorSkuId: skuIds[0] ?? "",
        evidence: { notes: reading.notes.slice() },
        fix: null,
        fixLabel: null,
        words: {
          title: phrase("rowQuestionTitle", { block: reading.column }),
          detail: [
            phrase("rowQuestionDetail", {
              block: reading.column,
              order: reading.rowOrder.join(", "),
              rows: {
                list: baseRows.map(({ row, shelves }) =>
                  phrase("rowOnShelves", {
                    row: row === "(no row)" ? phrase("rowMissing") : row,
                    shelves: phrase("shelves", { shelves: shelves.join(", ") }, shelves.length),
                  }),
                ),
              },
            }),
          ],
          stop: true,
        },
      });
    }
  }
  return [
    ...flags,
    ...sizeLayoutFlags(tierReadings, contract.itemValues),
    ...sizeTierFlags(items, draft, collapsed),
  ];
};

/**
 * Every line row offer this draft raises, taken. The Blocking list folds them into the tidy-ups
 * Studio handles itself, with nothing for the merchant to decide, so Finalize takes them whatever
 * the panel shows. Read on the draft as the panel's detection reads it, as the offers are raised.
 */
export const withRowLinesTaken = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
): StudioDraft =>
  rowLineFlags(items, draft, runs)
    .filter((flag) => flag.id.startsWith(ROW_LINE_PREFIX))
    .reduce(applyFix, draft);
